import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CrewMember, CsvImportResult, Document } from '../types';
import { crewService, CreateCrewMemberData, UpdateCrewMemberData } from '../services/crewService';
import { documentService } from '../services/documentService';
import { getCertificateStatusFromDocuments, CertStatus } from '../utils/certificateUtils';
import ASVerificationBadge from '../components/ASVerificationBadge';

const AS_NUMBER_REGEX = /^\d+$/;
const PLB_HEX_REGEX = /^[0-9A-Fa-f]{15}$/;
const PHONE_REGEX = /^(\+61|0)[2-9]\d{8}$/;

interface FormErrors { firstName?: string; lastName?: string; asNumber?: string; dateOfBirth?: string; plbHexNumber?: string; phoneNumber?: string; nextOfKinName?: string; nextOfKinRelationship?: string; nextOfKinPhone?: string; }

function validateForm(firstName: string, lastName: string, asNumber: string, dateOfBirth: string, plbHexNumber?: string, phoneNumber?: string, nextOfKin?: { name: string; relationship: string; phoneNumber: string }): FormErrors {
  const errors: FormErrors = {};
  if (!firstName.trim()) errors.firstName = 'First name is required.';
  if (!lastName.trim()) errors.lastName = 'Last name is required.';
  if (!asNumber.trim()) errors.asNumber = 'AS Number is required.';
  else if (!AS_NUMBER_REGEX.test(asNumber.trim())) errors.asNumber = 'AS Number must contain only digits.';
  if (!dateOfBirth) errors.dateOfBirth = 'Date of birth is required.';
  else { const dob = new Date(dateOfBirth); if (isNaN(dob.getTime()) || dob >= new Date()) errors.dateOfBirth = 'Date of birth must be in the past.'; }
  if (plbHexNumber?.trim() && !PLB_HEX_REGEX.test(plbHexNumber.trim())) errors.plbHexNumber = 'PLB hex must be 15 hex characters.';
  if (phoneNumber?.trim() && !PHONE_REGEX.test(phoneNumber.trim())) errors.phoneNumber = 'Invalid phone format. Use +61XXXXXXXXX or 0XXXXXXXXX.';
  if (nextOfKin && (nextOfKin.name || nextOfKin.relationship || nextOfKin.phoneNumber)) {
    if (!nextOfKin.name.trim()) errors.nextOfKinName = 'Name required.';
    if (!nextOfKin.relationship.trim()) errors.nextOfKinRelationship = 'Relationship required.';
    if (!nextOfKin.phoneNumber.trim()) errors.nextOfKinPhone = 'Phone required.';
    else if (!PHONE_REGEX.test(nextOfKin.phoneNumber.trim())) errors.nextOfKinPhone = 'Invalid phone format.';
  }
  return errors;
}

const CertIcon: React.FC<{ status: CertStatus }> = ({ status }) => {
  if (status === 'valid') return <span className="cert-tick" title="Valid certificate">✓</span>;
  if (status === 'expired') return <span className="cert-expired" title="Certificate expired">⚠️</span>;
  return null;
};

/** Modal for adding/editing crew members — no verification logic here */
const CrewFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingCrew?: CrewMember | null;
}> = ({ isOpen, onClose, onSave, editingCrew }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [asNumber, setAsNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [plbHexNumber, setPlbHexNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [nextOfKin, setNextOfKin] = useState({ name: '', relationship: '', phoneNumber: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (editingCrew) {
      setFirstName(editingCrew.firstName);
      setLastName(editingCrew.lastName);
      setAsNumber(editingCrew.asNumber);
      setDateOfBirth(editingCrew.dateOfBirth);
      setPlbHexNumber(editingCrew.plbHexNumber || '');
      setPhoneNumber(editingCrew.phoneNumber || '');
      setNextOfKin(editingCrew.nextOfKin || { name: '', relationship: '', phoneNumber: '' });
    } else {
      setFirstName(''); setLastName(''); setAsNumber(''); setDateOfBirth('');
      setPlbHexNumber(''); setPhoneNumber(''); setNextOfKin({ name: '', relationship: '', phoneNumber: '' });
      setErrors({}); setSubmitError(null);
    }
  }, [editingCrew, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const hasNok = nextOfKin.name || nextOfKin.relationship || nextOfKin.phoneNumber;
    const validationErrors = validateForm(firstName, lastName, asNumber, dateOfBirth, plbHexNumber, phoneNumber, hasNok ? nextOfKin : undefined);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      const hasNextOfKin = nextOfKin.name && nextOfKin.relationship && nextOfKin.phoneNumber;
      const nokData = hasNextOfKin ? { name: nextOfKin.name.trim(), relationship: nextOfKin.relationship, phoneNumber: nextOfKin.phoneNumber.trim() } : undefined;

      if (editingCrew) {
        const updateData: UpdateCrewMemberData = {
          firstName: firstName.trim(), lastName: lastName.trim(), asNumber: asNumber.trim(),
          dateOfBirth, plbHexNumber: plbHexNumber.trim() || undefined, phoneNumber: phoneNumber.trim() || undefined, nextOfKin: nokData
        };
        await crewService.updateCrewMember(editingCrew.crewId, updateData);
      } else {
        const createData: CreateCrewMemberData = {
          firstName: firstName.trim(), lastName: lastName.trim(), asNumber: asNumber.trim(),
          dateOfBirth, plbHexNumber: plbHexNumber.trim() || undefined, phoneNumber: phoneNumber.trim() || undefined, nextOfKin: nokData
        };
        await crewService.addCrewMember(createData);
      }
      onSave();
      onClose();
    } catch {
      setSubmitError(editingCrew ? 'Failed to update.' : 'Failed to add. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content crew-form-modal">
        <div className="modal-header">
          <h2>{editingCrew ? 'Edit Crew Member' : 'Add Crew Member'}</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="firstName">First Name *</label>
                <input id="firstName" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} />
                {errors.firstName && <span className="field-error">{errors.firstName}</span>}
              </div>
              <div className="form-field">
                <label htmlFor="lastName">Last Name *</label>
                <input id="lastName" type="text" value={lastName} onChange={e => setLastName(e.target.value)} />
                {errors.lastName && <span className="field-error">{errors.lastName}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="dateOfBirth">Date of Birth *</label>
                <input id="dateOfBirth" type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} />
                {errors.dateOfBirth && <span className="field-error">{errors.dateOfBirth}</span>}
              </div>
              <div className="form-field">
                <label htmlFor="asNumber">AS Number *</label>
                <input id="asNumber" type="text" value={asNumber} onChange={e => setAsNumber(e.target.value)} placeholder="123456" />
                {errors.asNumber && <span className="field-error">{errors.asNumber}</span>}
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="plbHexNumber">PLB Hex (Optional)</label>
              <input id="plbHexNumber" type="text" value={plbHexNumber} onChange={e => setPlbHexNumber(e.target.value)} placeholder="15 hex characters" />
              {errors.plbHexNumber && <span className="field-error">{errors.plbHexNumber}</span>}
            </div>
            <div className="form-field">
              <label htmlFor="phoneNumber">Phone (Optional)</label>
              <input id="phoneNumber" type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="+61412345678 or 0412345678" />
              {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
            </div>
            <h4>Next of Kin (Optional)</h4>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="nokName">Name</label>
                <input id="nokName" type="text" value={nextOfKin.name} onChange={e => setNextOfKin({ ...nextOfKin, name: e.target.value })} />
                {errors.nextOfKinName && <span className="field-error">{errors.nextOfKinName}</span>}
              </div>
              <div className="form-field">
                <label htmlFor="nokRelationship">Relationship</label>
                <select id="nokRelationship" value={nextOfKin.relationship} onChange={e => setNextOfKin({ ...nextOfKin, relationship: e.target.value })}>
                  <option value="">Select...</option>
                  <option value="spouse">Spouse</option>
                  <option value="partner">Partner</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="child">Child</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
                {errors.nextOfKinRelationship && <span className="field-error">{errors.nextOfKinRelationship}</span>}
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="nokPhone">Phone</label>
              <input id="nokPhone" type="tel" value={nextOfKin.phoneNumber} onChange={e => setNextOfKin({ ...nextOfKin, phoneNumber: e.target.value })} placeholder="+61 or 0X..." />
              {errors.nextOfKinPhone && <span className="field-error">{errors.nextOfKinPhone}</span>}
            </div>
            {submitError && <p className="error-message">{submitError}</p>}
          </div>
          <div className="modal-footer">
            <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-button" disabled={submitting}>
              {submitting ? 'Saving...' : (editingCrew ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CsvUpload: React.FC<{ onImportComplete: () => void }> = ({ onImportComplete }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.csv')) { setError('Please upload a CSV file.'); return; }
    setUploading(true); setError(null); setResult(null);
    try {
      const res = await crewService.bulkImport(file);
      setResult(res);
      if (res.successCount > 0) onImportComplete();
    } catch { setError('Failed to import CSV. Please try again.'); }
    finally { setUploading(false); }
  };

  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);
  const handleClick = () => fileInputRef.current?.click();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); };

  return (
    <div className="csv-upload" data-testid="csv-upload">
      <h3>Bulk Import</h3>
      <div className={`drop-zone ${dragging ? 'dragging' : ''}`} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={handleClick} data-testid="drop-zone">
        <input type="file" ref={fileInputRef} accept=".csv" onChange={handleChange} style={{ display: 'none' }} data-testid="file-input" />
        {uploading ? <p>Uploading...</p> : <p>Drag & drop CSV or click to select</p>}
      </div>
      {error && <p className="error-message" data-testid="csv-error">{error}</p>}
      {result && (
        <div className="import-result" data-testid="import-result">
          <p>Imported: {result.successCount}, Errors: {result.errorCount}</p>
          {result.errors.length > 0 && <ul>{result.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}</ul>}
        </div>
      )}
    </div>
  );
};

/** Crew list table with verify button, badge, and polling per row */
const CrewTable: React.FC<{
  crew: CrewMember[];
  documents: Document[];
  onEdit: (c: CrewMember) => void;
  onDelete: (id: string) => void;
  onRefresh: () => void;
}> = ({ crew, documents, onEdit, onDelete, onRefresh }) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, []);

  const handleDelete = async (member: CrewMember) => {
    if (!window.confirm(`Delete ${member.firstName} ${member.lastName}?`)) return;
    setDeletingId(member.crewId);
    try { await crewService.deleteCrewMember(member.crewId); onDelete(member.crewId); }
    catch { alert('Failed to delete.'); }
    finally { setDeletingId(null); }
  };

  const handleVerify = async (member: CrewMember) => {
    if (verifyingId) return; // one at a time
    setVerifyingId(member.crewId);
    setVerifyError(null);

    const prevVerifiedAt = member.verifiedAt;
    const prevMatchStatus = member.verificationMatchStatus;
    const prevUpdatedAt = member.updatedAt;

    try {
      await crewService.verifyAsNumber(member.crewId);
      const startTime = Date.now();
      const poll = async () => {
        if (Date.now() - startTime > 60000) {
          setVerifyingId(null);
          setVerifyError('Verification timed out. It may still be running — refresh later.');
          return;
        }
        try {
          const updated = await crewService.getCrewMember(member.crewId);
          const changed = updated.verifiedAt !== prevVerifiedAt
            || updated.verificationMatchStatus !== prevMatchStatus
            || updated.updatedAt !== prevUpdatedAt;
          if (changed) {
            setVerifyingId(null);
            onRefresh();
            return;
          }
        } catch { /* ignore polling errors */ }
        pollTimerRef.current = setTimeout(poll, 3000);
      };
      pollTimerRef.current = setTimeout(poll, 3000);
    } catch {
      setVerifyingId(null);
      setVerifyError('Failed to start verification.');
    }
  };

  if (crew.length === 0) {
    return (
      <div className="crew-table-empty">
        <p>No crew members yet. Click "Add Crew" to add your first crew member.</p>
      </div>
    );
  }

  return (
    <>
      {verifyError && <p className="error-message">{verifyError}</p>}
      <table className="crew-cert-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>AS Number</th>
            <th className="cert-col">SSSC</th>
            <th className="cert-col">First Aid</th>
            <th className="cert-col">Radio</th>
            <th className="actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {crew.map((member) => {
            const certStatus = getCertificateStatusFromDocuments(member.crewId, documents);
            const isDeleting = deletingId === member.crewId;
            const isVerifying = verifyingId === member.crewId;

            return (
              <tr key={member.crewId}>
                <td>{member.firstName} {member.lastName}</td>
                <td>
                  <div className="as-number-cell">
                    <span>{member.asNumber}</span>
                    {member.asNumber && (
                      <ASVerificationBadge
                        asVerified={member.asVerified}
                        membershipStatus={member.membershipStatus}
                        verificationMatchStatus={member.verificationMatchStatus}
                        verifying={isVerifying}
                        onClick={!isVerifying ? () => handleVerify(member) : undefined}
                      />
                    )}
                  </div>
                </td>
                <td className="cert-col"><CertIcon status={certStatus.sssc} /></td>
                <td className="cert-col"><CertIcon status={certStatus.firstAid} /></td>
                <td className="cert-col"><CertIcon status={certStatus.radio} /></td>
                <td className="actions-col actions-inline">
                  <button
                    className="btn-icon"
                    onClick={() => onEdit(member)}
                    title="Edit"
                    aria-label={`Edit ${member.firstName} ${member.lastName}`}
                  >✏️</button>
                  <button
                    className="btn-icon"
                    onClick={() => handleDelete(member)}
                    disabled={isDeleting}
                    title="Delete"
                    aria-label={`Delete ${member.firstName} ${member.lastName}`}
                  >{isDeleting ? '...' : '🗑️'}</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

const CrewPage: React.FC = () => {
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCrew, setEditingCrew] = useState<CrewMember | null>(null);
  const [showModal, setShowModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [crewData, docsData] = await Promise.all([
        crewService.getCrew(),
        documentService.getDocuments(),
      ]);
      setCrew(crewData);
      setDocuments(docsData);
    } catch {
      setError('Failed to load crew. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddClick = () => { setEditingCrew(null); setShowModal(true); };
  const handleEdit = (c: CrewMember) => { setEditingCrew(c); setShowModal(true); };
  const handleDelete = (id: string) => { setCrew(prev => prev.filter(c => c.crewId !== id)); };
  const handleModalClose = () => { setShowModal(false); setEditingCrew(null); };
  const handleSave = () => { loadData(); };

  return (
    <div className="crew-page" data-testid="crew-page">
      <div className="page-header">
        <h1>Crew Management</h1>
        <button type="button" className="primary-button" onClick={handleAddClick}>
          + Add Crew
        </button>
      </div>

      <CsvUpload onImportComplete={loadData} />

      <div className="crew-section">
        <h2>Your Crew</h2>
        {loading && <p data-testid="loading-indicator">Loading...</p>}
        {error && <p className="error-message" data-testid="load-error">{error}</p>}
        {!loading && !error && (
          <CrewTable
            crew={crew}
            documents={documents}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRefresh={loadData}
          />
        )}
      </div>

      <CrewFormModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSave={handleSave}
        editingCrew={editingCrew}
      />
    </div>
  );
};

export default CrewPage;
